import translations from '#lib/data/translations.js';
import * as env from '$app/env/public';
export const LANG = env.PUBLIC_LANG;

/**
 * Translates a given key using the provided locale and replaces any template variables.
 *
 * @param {string} locale - The locale to translate to.
 * @param {string} key - The translation key.
 * @param {Object} vars - The template variables to replace in the translation string.
 * @returns {string} - The translated string.
 * @throws Will throw an error if the key or locale is not provided.
 * @throws Will throw an error if the translation is not found.
 * 
 * @example
 * translations.en.greeting = "Hello, {{name}}!"
 * translate('en', 'greeting', { name: 'John' }); // "Hello, John!"
 */
function translate(lang, key, vars = {}) {
  // Let's throw some errors if we're trying to use keys/locales that don't exist.
  // We could improve this by using Typescript and/or fallback values.
  if (!key) {
    console.log('no key provided to t()');
    return "no key provided to t()";
  }
  if (!lang) throw new Error(`no lang provided: "${lang}"`);
 
  // Grab the translation from the translations object.
  let text = translations[lang][key];

  // If no translation is available yet, we return the key.
  if (!text) return key;

  // Replace any passed in variables in the translation string. The value comes from a function so
  // `$&`, `$'` or `$$` in it (a message, a name) are inserted as written, not as replacement patterns.
  Object.keys(vars).map((k) => {
    const regex = new RegExp(`{{${k}}}`, "g");
    text = text.replace(regex, () => vars[k]);
  });

  return text;
}


// Fix for: Pre-transform error: `$derived(...)` can only be used as a variable declaration initializer or a class field
// And fix for: Cannot export derived state from a module. To expose the current derived value, export a function returning its value

/**
 * Creates a translation function that reactively updates with language changes.
 * 
 * @returns {(key: string, vars?: Object, lang?: string) => string} A function that translates keys
 */
function create_translator() {
  return (key, vars = {}, lang) => {
    const _lang = LANG ?? lang;
    return translate(_lang, key, vars);
  };
}

// Create a reactive translator using Svelte 5's derived rune
const translator = $derived.by(create_translator);

/**
 * Translates a key into the current language, with optional variable replacement.
 * Updates reactively when the language changes.
 * 
 * @param {string} key - The translation key to look up
 * @param {Object} [vars={}] - Variables to replace in the translation string
 * @param {string} [lang] - Optional override for the current language
 * @returns {string} The translated string
 * 
 * @example
 * import { t } from '#lib/i18n.svelte.js';
 * 
 * Basic usage
 * t('greeting'); // "Hello"
 * 
 * With variables
 * t('welcome', { name: 'John' }); // "Welcome, John!"
 * 
 * With language override
 * t('greeting', {}, 'de'); // "Moin Moin"
 */
export function t(key, vars = {}, lang) {
  return translator(key, vars, lang);
}

export default t;